import {
  Body1,
  Button,
  Caption1,
  Divider,
  Link,
  NavDrawer,
  NavDrawerBody,
  NavDrawerHeader,
  Switch,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { ChevronLeft20Regular, Dismiss20Regular, OpenRegular } from '@fluentui/react-icons'
import { use, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DOCS_BASE_URL, TEAMS_CHANNEL_URL } from '../../constants/urls'
import { ChatSessionContext } from '../../contexts'
import { useTheme } from '../../hooks'
import { LanguageSubmenu } from './Menu/LanguageSubmenu'
import { MainMenu } from './Menu/MainMenu'
import { ThemeSubmenu } from './Menu/ThemeSubmenu'
import type { SubmenuType } from './types'

const useStyles = makeStyles({
  drawer: {
    width: 'calc(100% - 70px)',
    background: tokens.colorNeutralBackground4,
    borderRadius: `0 ${tokens.borderRadiusXLarge} ${tokens.borderRadiusXLarge} 0`,
  },
  drawerBody: {
    padding: '0',
  },
  header: {
    display: 'flex',
    alignItems: 'start',
    justifyContent: 'center',
    paddingInlineStart: tokens.spacingHorizontalS,
    paddingInlineEnd: tokens.spacingHorizontalMNudge,
    paddingBlockStart: tokens.spacingVerticalS,
    paddingBlockEnd: tokens.spacingVerticalS,
    minHeight: tokens.spacingVerticalXXXL,
  },
  headerIcon: {
    padding: `${tokens.spacingVerticalMNudge} ${tokens.spacingHorizontalMNudge}`,
    minWidth: tokens.spacingVerticalXXXL,
    height: tokens.spacingVerticalXXXL,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.borderRadiusMedium,
  },
  divider: {
    marginBlock: tokens.spacingVerticalL,
    flexGrow: 0,
  },
  externalLink: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    paddingInline: tokens.spacingHorizontalL,
  },
  externalLinksContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  toggleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalMNudge} ${tokens.spacingHorizontalL}`,
  },
  toggleText: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  toggleDescription: {
    color: tokens.colorNeutralForeground3,
  },
  toggleSwitch: {
    '& .fui-Switch__indicator': {
      margin: `0 ${tokens.spacingVerticalXS} ${tokens.spacingHorizontalXS}`,
      color: tokens.colorNeutralForeground3,
    },
    '& .fui-Switch__indicator > *': { transform: 'translateX(0)' },
  },
})

type SettingsSidebarProps = {
  isOpen: boolean
  onClose: () => void
}

const SettingsSidebar = ({ isOpen, onClose }: SettingsSidebarProps) => {
  const classes = useStyles()
  const { themeMode } = useTheme()
  const { t, i18n } = useTranslation()
  const [activeSubmenu, setActiveSubmenu] = useState<SubmenuType>(null)
  const [currentTheme, setCurrentTheme] = useState(themeMode)
  const chatSession = use(ChatSessionContext)

  const handleBackClick = () => {
    setActiveSubmenu(null)
  }

  const handleClose = useCallback(() => {
    setActiveSubmenu(null)
    onClose()
  }, [onClose])

  const isShowingSubmenu = activeSubmenu !== null

  const getSelectedValue = useCallback((): string => {
    // resolvedLanguage strips region variants (en-US → en) to match NavItem values
    if (activeSubmenu === 'language') return i18n.resolvedLanguage ?? i18n.language
    if (activeSubmenu === 'theme') return currentTheme
    return ''
  }, [activeSubmenu, i18n.resolvedLanguage, i18n.language, currentTheme])

  const renderSubmenu = useCallback(() => {
    switch (activeSubmenu) {
      case 'language':
        return <LanguageSubmenu onClose={handleClose} />
      case 'theme':
        return <ThemeSubmenu setCurrentTheme={setCurrentTheme} onClose={handleClose} />
      default:
        return <MainMenu setActiveSubmenu={setActiveSubmenu} />
    }
  }, [activeSubmenu, setCurrentTheme, handleClose])

  return (
    <NavDrawer
      role="navigation"
      selectedValue={getSelectedValue()}
      type="overlay"
      open={isOpen}
      onOpenChange={(_, { open }) => !open && onClose()}
      className={classes.drawer}>
      <NavDrawerHeader className={classes.header}>
        <Button
          appearance="subtle"
          aria-label={isShowingSubmenu ? t('settings.back') : t('close')}
          icon={isShowingSubmenu ? <ChevronLeft20Regular /> : <Dismiss20Regular />}
          onClick={isShowingSubmenu ? handleBackClick : handleClose}
          className={classes.headerIcon}
        />
      </NavDrawerHeader>
      <NavDrawerBody className={classes.drawerBody}>
        {renderSubmenu()}
        {!isShowingSubmenu && (
          <>
            <Divider className={classes.divider} />
            <div className={classes.toggleRow}>
              <div className={classes.toggleText}>
                <Body1 as="p">{t('settings.keepMessage')}</Body1>
                <Caption1 as="p" className={classes.toggleDescription}>
                  {t('settings.keepMessageDesc')}
                </Caption1>
              </div>
              <Switch
                checked={chatSession?.keepMessageOnSend ?? false}
                onChange={(_, data) => chatSession?.setKeepMessageOnSend(data.checked)}
                className={classes.toggleSwitch}
                aria-label={t('settings.keepMessage')}
              />
            </div>
            <Divider className={classes.divider} />
            <div className={classes.externalLinksContainer}>
              <Link
                as="a"
                href={TEAMS_CHANNEL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.externalLink}>
                <Body1>{t('home.teamsChannel')}</Body1>
                <OpenRegular fontSize={20} aria-hidden="true" />
              </Link>
              <Link
                as="a"
                href={DOCS_BASE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.externalLink}>
                <Body1>{t('home.userDocumentation')}</Body1>
                <OpenRegular fontSize={20} aria-hidden="true" />
              </Link>
            </div>
          </>
        )}
      </NavDrawerBody>
    </NavDrawer>
  )
}

export { SettingsSidebar }
